<template>
    <div class="row">
        <div class="col-12 mb-3">
            <table class="table table-striped">
                <thead>
                <tr>
                    <th>{{ $t('form.product') }}</th>
                    <th class="text-end">{{ $t('form.quantity') }}</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="item in getItems">
                    <td style="vertical-align: middle">
                        {{ item.product_name }} - <b>{{ item.product_size }}</b>
                    </td>
                    <td class="pe-3">
                        <CounterWithButtons
                            :quantity="getItemQuantity(item)"
                            :minCondition="getItemQuantity(item) === 0"
                            :maxCondition="getItemQuantity(item) === item.quantity"
                            @onIncrease="() => addItem(item)"
                            @onDecrease="() => removeItem(item)"
                        />
                    </td>
                </tr>
                </tbody>
            </table>

            <FormError v-if="form.errors.items" class="d-block">
                {{ form.errors.items }}
            </FormError>
        </div>

        <div class="col-12 mb-3">
            <SignaturePad v-model:modelValue="form.sign">
                <FormError v-if="form.errors.sign">
                    {{ form.errors.sign }}
                </FormError>
            </SignaturePad>
        </div>

        <div class="col-12 text-end">
            <SubmitButton
                v-if="form.sign"
                @click.prevent="onVerify"
                :disabled="form.processing"
            />
        </div>
    </div>

    <VerifyModal
        ref="verifyModalRef"
        @process="onProcessForm"
    />
</template>

<script setup lang="ts">
import type {
    CustomerInterface,
    IssuedItemLogInterface,
    IssuedLogInterface
} from "@/components/helpers/Interfaces.ts";
import {useForm} from "@inertiajs/vue3";
import {computed, ref} from "vue";
import SubmitButton from "@/components/buttons/SubmitButton.vue";
import FormError from "@/components/elements/FormError.vue";
import VerifyModal from "@/components/elements/VerifyModal.vue";
import SignaturePad from "@/components/elements/SignaturePad.vue";
import CounterWithButtons from "@/components/elements/CounterWithButtons.vue";

interface item  {
    product_id: number,
    product_size_id: number,
    quantity: number,
    product_name: string
    product_size: string
}

const { customer, allItems } = defineProps<{
    customer: CustomerInterface,
    allItems: IssuedLogInterface[]
}>()

const getItems = computed(() => {
    const byProductId = new Map<number, item>()

    allItems.forEach(log => {
        const issueType = log.issue_type?.toLowerCase()
        log.items.forEach((issuedItem: IssuedItemLogInterface) => {
            let existing = byProductId.get(issuedItem.product_id)
            if (!existing) {
                existing = {
                    product_id: issuedItem.product_id,
                    product_size_id: issuedItem.product_size_id,
                    quantity: 0,
                    product_name: issuedItem.product.name,
                    product_size: issuedItem.size.size.toString()
                }
                byProductId.set(issuedItem.product_id, existing)
            }

            if (issueType === "devolution") {
                existing.quantity += issuedItem.quantity
            } else {
                existing.quantity += issuedItem.quantity
            }
        })
    })

    return Array.from(byProductId.values())
})

function addItem(product: item) {
    const item = form.items.find(
        i =>
            i.product_id === product.product_id &&
            i.product_size_id === product.product_size_id
    )

    if (item) {
        item.quantity += 1
    } else {
        form.items.push({
            product_id: product.product_id,
            product_size_id: product.product_size_id,
            quantity: 1
        })
    }
}

function removeItem(product: item) {
    const index = form.items.findIndex(
        i =>
            i.product_id === product.product_id &&
            i.product_size_id === product.product_size_id
    )

    if (index === -1) return

    const item = form.items[index]
    item.quantity -= 1

    if (item.quantity <= 0) {
        form.items.splice(index, 1)
    }
}

function getItemQuantity(product: item) {
    const item = form.items.find(
        i =>
            i.product_id === product.product_id &&
            i.product_size_id === product.product_size_id
    )

    return item ? item.quantity : 0
}


const form = useForm({
    customer_id: customer.id,
    customer_uuid: customer.uuid,
    customer_name: customer.name,
    customer_lastname: customer.lastname,
    customer_document: customer.document,
    permission_id: customer.permissionId,
    sign: null,
    items: []
})

const verifyModalRef = ref<InstanceType<typeof VerifyModal> |null>(null)

const onVerify = () => {
    verifyModalRef.value.openModal()
}

const onProcessForm = () => {
    verifyModalRef.value.closeModal();
    form.post(`/issueLogs/devolution/${customer.id}`)
}
</script>

<style scoped>

</style>
